import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Menu,
    MenuItem,
    Typography,
    ListItemIcon,
    ListItemText,
    Divider,
    Button,
    Avatar
} from '@mui/material';
import {
    Add,
    FolderOpen,
    Save,
    SaveAs,
    FileUpload,
    Info,
    Undo,
    Redo,
    Settings as SettingsIcon,
    Visibility,
    ThreeDRotation,
    PlayArrow,
    Stop,
    Cable,
    GamepadOutlined,
    Print,
    Code,
    DataObject,
    SmartToy,
    HelpOutline,
    Dashboard,
    AccountTree,
    ViewInAr,
    SmartToy as RobotIcon,
    Monitor,
    Computer,
    Person,
    Terminal,
    Description,
    Extension,
    Close,
    ContentCopy,
    Edit,
    Delete,
    History,
    CheckCircle,
    FlightTakeoff,
    Water,
    DirectionsCar,
    PrecisionManufacturing,
    Group,
    ContentCut,
    ContentPaste,
    FindReplace,
    SelectAll,
    ZoomIn,
    ZoomOut,
    FitScreen,
    Fullscreen,
    FullscreenExit,
    CenterFocusStrong,
    GridOn,
    Layers,
    Palette,
    Refresh,
    FilterList,
    Sort,
    ViewModule,
    ViewList,
    ViewComfy,
    ViewQuilt,
    Build,
    BugReport,
    Speed,
    Analytics,
    Tune,
    Download,
    RestartAlt,
    Pause,
    Store,
    Update,
    Category,
    Star,
    CloudDownload,
    SettingsApplications,
    DarkMode,
    LightMode,
    Notifications,
    Language,
    Storage,
    Security,
    Backup,
    Restore,
    ClearAll,
    Wifi,
    CloudSync,
    PhotoCamera,
    Image,
    AccountCircle,
    Logout,
    VpnKey,
    Badge,
    Timeline,
    Edit as EditIcon,
    Psychology,
    School,
    Sensors,
    TrendingUp,
    Folder,
    Map,
    Route,
    QuestionAnswer,
    Keyboard,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { API_PREFIX } from 'src/config/api';
import { addNotification, setTheme } from 'src/store/slices/uiSlice';
import { setCurrentProject, duplicateProject, deleteProject, clearRecentProjects } from 'src/store/slices/projectSlice';
import AboutDialog from './AboutDialog';

interface MenuBarProps {
    onNewProject: () => void;
    onOpenProject: () => void;
    onSaveProject: () => void;
    onSaveAs?: () => void;
    onDownloadTlx?: () => void;
    onExport?: () => void;
    onUndo?: () => void;
    onRedo?: () => void;
    onProjectSettings?: () => void;
    onTogglePanel?: (panel: string) => void;
    on3DViewOptions?: () => void;
    onRunSimulation?: () => void;
    onStopSimulation?: () => void;
    onConnectRobot?: () => void;
    onManualControl?: () => void;
    onDocumentation?: () => void;
    onNavigate?: (path: string) => void;
    onTakeTour?: () => void;
    currentTheme?: 'light' | 'dark';
}

const MenuBar: React.FC<MenuBarProps> = ({
    onNewProject,
    onOpenProject,
    onSaveProject,
    onSaveAs,
    onDownloadTlx,
    onExport,
    onUndo,
    onRedo,
    onProjectSettings,
    onTogglePanel,
    on3DViewOptions,
    onRunSimulation,
    onStopSimulation,
    onConnectRobot,
    onManualControl,
    onDocumentation,
    onNavigate,
    onTakeTour,
    currentTheme = 'light'
}) => {
    const dispatch = useAppDispatch();
    const { currentProject, projects, recentProjects, templates } = useAppSelector(state => state.project);
    const [projectAnchor, setProjectAnchor] = useState<HTMLElement | null>(null);
    const [newFromTemplateAnchor, setNewFromTemplateAnchor] = useState<HTMLElement | null>(null);
    const [editAnchor, setEditAnchor] = useState<HTMLElement | null>(null);
    const [viewAnchor, setViewAnchor] = useState<HTMLElement | null>(null);
    const [toolsAnchor, setToolsAnchor] = useState<HTMLElement | null>(null);
    const [marketplaceAnchor, setMarketplaceAnchor] = useState<HTMLElement | null>(null);
    const [windowAnchor, setWindowAnchor] = useState<HTMLElement | null>(null);
    const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
    const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
    const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
    const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
    const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { theme } = useAppSelector(state => state.ui);

    const handleProjectClick = (event: React.MouseEvent<HTMLElement>) => {
        setProjectAnchor(event.currentTarget);
    };

    const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
        setEditAnchor(event.currentTarget);
    };

    const handleViewClick = (event: React.MouseEvent<HTMLElement>) => {
        setViewAnchor(event.currentTarget);
    };

    const handleToolsClick = (event: React.MouseEvent<HTMLElement>) => {
        setToolsAnchor(event.currentTarget);
    };

    const handleMarketplaceClick = (event: React.MouseEvent<HTMLElement>) => {
        setMarketplaceAnchor(event.currentTarget);
    };

    const handleWindowClick = (event: React.MouseEvent<HTMLElement>) => {
        setWindowAnchor(event.currentTarget);
    };

    const handleHelpClick = (event: React.MouseEvent<HTMLElement>) => {
        setHelpAnchor(event.currentTarget);
    };

    const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
        setExportAnchor(event.currentTarget);
    };

    const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
        setSettingsAnchor(event.currentTarget);
    };

    const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
        setProfileAnchor(event.currentTarget);
    };

    const handleClose = () => {
        setProjectAnchor(null);
        setEditAnchor(null);
        setViewAnchor(null);
        setToolsAnchor(null);
        setMarketplaceAnchor(null);
        setWindowAnchor(null);
        setHelpAnchor(null);
        setExportAnchor(null);
        setNewFromTemplateAnchor(null);
        setSettingsAnchor(null);
        setProfileAnchor(null);
    };

    // Project Menu Handlers
    const handleNewProject = () => {
        handleClose();
        onNewProject();
    };

    const handleNewFromTemplate = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setNewFromTemplateAnchor(event.currentTarget);
    };

    const handleCreateFromTemplate = (templateId: string) => {
        handleClose();
        const template = templates.find(t => t.id === templateId);
        if (template) {
            dispatch(addNotification({
                type: 'info',
                title: 'Create from Template',
                message: `Creating project from template: ${template.name}`
            }));
            // This would trigger the template creation flow
            onNewProject();
        }
    };

    const handleOpenRecentProject = (projectId: string) => {
        handleClose();
        const project = projects.find(p => p.id === projectId);
        if (project) {
            dispatch(setCurrentProject(project));
            dispatch(addNotification({
                type: 'success',
                title: 'Project Opened',
                message: `Opened project: ${project.name}`
            }));
        }
    };

    const handleCloseProject = () => {
        handleClose();
        if (currentProject) {
            dispatch(setCurrentProject(null));
            dispatch(addNotification({
                type: 'info',
                title: 'Project Closed',
                message: `Closed project: ${currentProject.name}`
            }));
        }
    };

    const handleDuplicateProject = () => {
        handleClose();
        if (currentProject) {
            dispatch(duplicateProject(currentProject.id));
            dispatch(addNotification({
                type: 'success',
                title: 'Project Duplicated',
                message: `Created copy of: ${currentProject.name}`
            }));
        }
    };

    const handleRenameProject = () => {
        handleClose();
        const newName = window.prompt('Enter new project name:', currentProject?.name || '');
        if (newName && currentProject) {
            // This would update the project name
            dispatch(addNotification({
                type: 'info',
                title: 'Rename Project',
                message: 'Rename functionality will be implemented'
            }));
        }
    };

    const handleDeleteProject = () => {
        handleClose();
        if (currentProject) {
            const confirmed = window.confirm(`Are you sure you want to delete "${currentProject.name}"? This action cannot be undone.`);
            if (confirmed) {
                dispatch(deleteProject(currentProject.id));
                dispatch(addNotification({
                    type: 'warning',
                    title: 'Project Deleted',
                    message: `Deleted project: ${currentProject.name}`
                }));
            }
        }
    };

    const handleClearRecentProjects = () => {
        handleClose();
        dispatch(clearRecentProjects());
        dispatch(addNotification({
            type: 'info',
            title: 'Recent Projects Cleared',
            message: 'Recent projects list has been cleared'
        }));
    };

    // Get recent projects with full project data
    const recentProjectsData = recentProjects
        .map(id => projects.find(p => p.id === id))
        .filter((p): p is NonNullable<typeof p> => p !== undefined)
        .slice(0, 5);

    const templateIcons: Record<string, React.ReactNode> = {
        drone_delivery: <FlightTakeoff fontSize="small" />,
        underwater_inspection: <Water fontSize="small" />,
        ground_robot: <DirectionsCar fontSize="small" />,
        manipulator: <PrecisionManufacturing fontSize="small" />,
        multi_robot: <Group fontSize="small" />,
        custom: <Extension fontSize="small" />
    };

    const handleOpenProject = () => {
        handleClose();
        fileInputRef.current?.click();
    };

    const handleSave = () => {
        handleClose();
        if (currentProject) {
            onSaveProject();
        } else {
            dispatch(addNotification({
                type: 'warning',
                title: 'No Project',
                message: 'Please create or open a project first.'
            }));
        }
    };

    const handleSaveAs = () => {
        handleClose();
        if (currentProject && onSaveAs) {
            onSaveAs();
        } else {
            dispatch(addNotification({
                type: 'warning',
                title: 'No Project',
                message: 'Please create or open a project first.'
            }));
        }
    };

    const handleDownloadTlx = () => {
        handleClose();
        if (currentProject && onDownloadTlx) {
            onDownloadTlx();
        } else {
            dispatch(addNotification({
                type: 'warning',
                title: 'No Project',
                message: 'Please create or open a project first.'
            }));
        }
    };

    const handleExportMenu = () => {
        handleClose();
        if (onExport) {
            onExport();
        }
    };

    // Edit Menu Handlers
    const handleUndo = () => {
        handleClose();
        if (onUndo) {
            onUndo();
        } else {
            dispatch(addNotification({
                type: 'info',
                title: 'Undo',
                message: 'Undo functionality not implemented yet.'
            }));
        }
    };

    const handleRedo = () => {
        handleClose();
        if (onRedo) {
            onRedo();
        } else {
            dispatch(addNotification({
                type: 'info',
                title: 'Redo',
                message: 'Redo functionality not implemented yet.'
            }));
        }
    };

    const handleCut = () => {
        handleClose();
        dispatch(addNotification({
            type: 'info',
            title: 'Cut',
            message: 'Cut functionality will be implemented for selected nodes/elements.'
        }));
    };

    const handleCopy = () => {
        handleClose();
        dispatch(addNotification({
            type: 'info',
            title: 'Copy',
            message: 'Copy functionality will be implemented for selected nodes/elements.'
        }));
    };

    const handlePaste = () => {
        handleClose();
        dispatch(addNotification({
            type: 'info',
            title: 'Paste',
            message: 'Paste functionality will be implemented for copied nodes/elements.'
        }));
    };

    const handleSelectAll = () => {
        handleClose();
        dispatch(addNotification({
            type: 'info',
            title: 'Select All',
            message: 'Select all functionality will be implemented.'
        }));
    };

    const handleFindReplace = () => {
        handleClose();
        dispatch(addNotification({
            type: 'info',
            title: 'Find & Replace',
            message: 'Find and replace dialog coming soon.'
        }));
    };

    const handleDelete = () => {
        handleClose();
        dispatch(addNotification({
            type: 'info',
            title: 'Delete',
            message: 'Delete selected items functionality will be implemented.'
        }));
    };

    const handleProjectSettings = () => {
        handleClose();
        if (onProjectSettings) {
            onProjectSettings();
        } else {
            dispatch(addNotification({
                type: 'info',
                title: 'Project Settings',
                message: 'Project settings dialog coming soon.'
            }));
        }
    };

    // View Menu Handlers
    const [zoomLevel, setZoomLevel] = useState(100);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Listen for fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 10, 200));
        dispatch(addNotification({
            type: 'info',
            title: 'Zoom In',
            message: `Zoom: ${zoomLevel + 10}%`
        }));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 10, 25));
        dispatch(addNotification({
            type: 'info',
            title: 'Zoom Out',
            message: `Zoom: ${zoomLevel - 10}%`
        }));
    };

    const handleZoomFit = () => {
        setZoomLevel(100);
        dispatch(addNotification({
            type: 'info',
            title: 'Fit to Screen',
            message: 'View adjusted to fit screen.'
        }));
    };

    const handleZoomReset = () => {
        setZoomLevel(100);
        dispatch(addNotification({
            type: 'info',
            title: 'Reset Zoom',
            message: 'Zoom reset to 100%.'
        }));
    };

    const handleToggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => {
                dispatch(addNotification({
                    type: 'error',
                    title: 'Fullscreen Error',
                    message: 'Unable to enter fullscreen mode.'
                }));
            });
        } else {
            document.exitFullscreen();
        }
        handleClose();
    };

    const handleCenterView = () => {
        dispatch(addNotification({
            type: 'info',
            title: 'Center View',
            message: 'View centered on canvas.'
        }));
    };

    const handleTogglePanel = (panel: string) => {
        handleClose();
        if (onTogglePanel) {
            onTogglePanel(panel);
        } else {
            dispatch(addNotification({
                type: 'info',
                title: 'Toggle Panel',
                message: `${panel} panel toggle coming soon.`
            }));
        }
    };

    const handle3DViewOptions = () => {
        handleClose();
        if (on3DViewOptions) {
            on3DViewOptions();
        } else {
            dispatch(addNotification({
                type: 'info',
                title: '3D View Options',
                message: '3D view options dialog coming soon.'
            }));
        }
    };

    // Navigation Handlers
    const handleNavigateTo = (path: string, title: string) => {
        handleClose();
        if (onNavigate) {
            onNavigate(path);
            dispatch(addNotification({
                type: 'info',
                title: 'Navigation',
                message: `Switched to ${title}`
            }));
        }
    };

    // Robot Menu Handlers
    const handleRunSimulation = () => {
        handleClose();
        if (onRunSimulation) {
            onRunSimulation();
        } else {
            dispatch(addNotification({
                type: 'success',
                title: 'Run Simulation',
                message: 'Simulation started successfully.'
            }));
        }
    };

    const handleStopSimulation = () => {
        handleClose();
        if (onStopSimulation) {
            onStopSimulation();
        } else {
            dispatch(addNotification({
                type: 'warning',
                title: 'Stop Simulation',
                message: 'Simulation stopped.'
            }));
        }
    };

    const handleConnectRobot = () => {
        handleClose();
        if (onConnectRobot) {
            onConnectRobot();
        } else {
            dispatch(addNotification({
                type: 'info',
                title: 'Connect Robot',
                message: 'Robot connection dialog coming soon.'
            }));
        }
    };

    const handleManualControl = () => {
        handleClose();
        if (onManualControl) {
            onManualControl();
        } else {
            dispatch(addNotification({
                type: 'info',
                title: 'Manual Control',
                message: 'Manual robot control coming soon.'
            }));
        }
    };

    // Help Menu Handlers
    const handleDocumentation = () => {
        handleClose();
        if (onNavigate) {
            onNavigate('/help/documentation');
        } else if (onDocumentation) {
            onDocumentation();
        } else {
            dispatch(addNotification({
                type: 'info',
                title: 'Documentation',
                message: 'Opening Telix documentation...'
            }));
        }
    };

    const handleAbout = () => {
        handleClose();
        setAboutDialogOpen(true);
    };

    // Export Tools Handlers
    const handleExportSTL = () => {
        handleClose();
        dispatch(addNotification({
            type: 'success',
            title: 'Export STL',
            message: 'Robot design exported as STL files for 3D printing.'
        }));
    };

    const handleGenerateURDF = () => {
        handleClose();
        dispatch(addNotification({
            type: 'success',
            title: 'Generate URDF',
            message: 'URDF file generated successfully.'
        }));
    };

    const handleExportCode = () => {
        handleClose();
        dispatch(addNotification({
            type: 'success',
            title: 'Export Code',
            message: 'ROS2 Python package exported successfully.'
        }));
    };

    const handleAITrainingData = () => {
        handleClose();
        dispatch(addNotification({
            type: 'info',
            title: 'AI Training Data Viewer',
            message: 'Opening AI training data viewer...'
        }));
    };

    const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const projectData = JSON.parse(e.target?.result as string);
                    // POST the imported project to backend to persist it
                    const response = await fetch(`${API_PREFIX}/api/projects`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(projectData)
                    });
                    if (!response.ok) throw new Error('Failed to import project to server');
                    const saved = await response.json();
                    // set as current project in redux
                    dispatch(setCurrentProject(saved));
                    dispatch(addNotification({
                        type: 'success',
                        title: 'Project Imported',
                        message: `Project "${saved.name || saved.id}" imported and saved.`
                    }));
                } catch (error: any) {
                    dispatch(addNotification({
                        type: 'error',
                        title: 'Import Failed',
                        message: error?.message || 'Invalid project file format.'
                    }));
                }
            };
            reader.readAsText(file);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const menuButtonStyles = {
        minWidth: 'auto',
        px: 1.5,
        py: 0.25,
        fontSize: '0.875rem',
        textTransform: 'none' as const,
        color: 'text.primary',
        '&:hover': {
            bgcolor: currentTheme === 'dark' ? 'grey.800' : 'grey.100'
        }
    };

    const menuPaperProps = {
        sx: {
            bgcolor: currentTheme === 'dark' ? 'grey.800' : 'background.paper',
            color: currentTheme === 'dark' ? 'grey.100' : 'text.primary'
        }
    };

    const menuItemStyles = {
        fontSize: '0.875rem',
        '&:hover': {
            bgcolor: currentTheme === 'dark' ? 'grey.700' : 'grey.100'
        }
    };

    const dividerStyles = {
        my: 0.5,
        borderColor: currentTheme === 'dark' ? 'grey.600' : 'grey.200'
    };

    return (
        <>
            <Box
                data-tour="menu-bar"
                sx={{
                    display: 'flex',
                    bgcolor: currentTheme === 'dark' ? 'grey.900' : 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: currentTheme === 'dark' ? 'grey.700' : 'grey.200',
                    minHeight: 32,
                    alignItems: 'center',
                    px: 1
                }}
            >
                {/* Telix Brand */}
                <Typography
                    variant="h6"
                    sx={{
                        px: 2,
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'primary.main'
                    }}
                >
                    Telix Lab
                </Typography>

                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                        mx: 1,
                        height: 20,
                        alignSelf: 'center',
                        borderColor: currentTheme === 'dark' ? 'grey.700' : 'grey.300'
                    }}
                />

                {/* Project Menu (leftmost) */}
                <Button variant="text" size="small" sx={menuButtonStyles} onClick={handleProjectClick}>
                    Project
                </Button>
                <Menu
                    anchorEl={projectAnchor}
                    open={Boolean(projectAnchor)}
                    onClose={handleClose}
                    MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                    elevation={1}
                    PaperProps={menuPaperProps}
                    sx={{ '& .MuiPaper-root': { minWidth: 240 } }}
                >
                    {/* Current Project Info */}
                    {currentProject && (
                        <>
                            <Box sx={{ px: 2, py: 1, bgcolor: currentTheme === 'dark' ? 'grey.800' : 'grey.100' }}>
                                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                                    {currentProject.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {currentProject.type.replace('_', ' ')} • {currentProject.status}
                                </Typography>
                            </Box>
                            <Divider sx={dividerStyles} />
                        </>
                    )}

                    {/* Recent Projects Section */}
                    {recentProjectsData.length > 0 && (
                        <>
                            <Box sx={{ px: 2, py: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                    Recent Projects
                                </Typography>
                            </Box>
                            {recentProjectsData.map((project) => (
                                <MenuItem
                                    key={project.id}
                                    onClick={() => handleOpenRecentProject(project.id)}
                                    sx={{
                                        ...menuItemStyles,
                                        bgcolor: currentProject?.id === project.id
                                            ? (currentTheme === 'dark' ? 'grey.700' : 'grey.200')
                                            : 'transparent'
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                                        {currentProject?.id === project.id ? (
                                            <CheckCircle fontSize="small" color="primary" />
                                        ) : (
                                            <History fontSize="small" />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={project.name}
                                        secondary={new Date(project.modified).toLocaleDateString()}
                                    />
                                </MenuItem>
                            ))}
                            <MenuItem onClick={handleClearRecentProjects} sx={menuItemStyles}>
                                <ListItemText primary="Clear Recent Projects" primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }} />
                            </MenuItem>
                            <Divider sx={dividerStyles} />
                        </>
                    )}

                    {/* New Project Options */}
                    <MenuItem onClick={handleNewProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Add fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>New Project (Ctrl+N)</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={handleNewFromTemplate}
                        sx={menuItemStyles}
                    >
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Extension fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>New from Template</ListItemText>
                        <Box sx={{ ml: 'auto' }}>›</Box>
                    </MenuItem>
                    <MenuItem onClick={handleOpenProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <FolderOpen fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Open Project... (Ctrl+O)</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Save Options */}
                    <MenuItem onClick={handleSave} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Save fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Save (Ctrl+S)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleSaveAs} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SaveAs fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Save As... (Ctrl+Shift+S)</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Project Management */}
                    <MenuItem onClick={handleCloseProject} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Close fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Close Project (Ctrl+W)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDuplicateProject} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ContentCopy fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Duplicate Project (Ctrl+D)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleRenameProject} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Edit fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Rename Project...</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleProjectSettings} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SettingsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Project Properties...</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Export/Import */}
                    <MenuItem onClick={handleExportClick} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <FileUpload fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Export Project</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Delete (destructive action) */}
                    <MenuItem onClick={handleDeleteProject} disabled={!currentProject} sx={{ ...menuItemStyles, color: 'error.main' }}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'error.main' }}>
                            <Delete fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Delete Project...</ListItemText>
                    </MenuItem>
                </Menu>

                {/* New from Template Submenu */}
                <Menu
                    anchorEl={newFromTemplateAnchor}
                    open={Boolean(newFromTemplateAnchor)}
                    onClose={() => setNewFromTemplateAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                    elevation={1}
                    PaperProps={menuPaperProps}
                >
                    {templates.map((template) => (
                        <MenuItem
                            key={template.id}
                            onClick={() => handleCreateFromTemplate(template.id)}
                            sx={menuItemStyles}
                        >
                            <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                                {templateIcons[template.type] || <Extension fontSize="small" />}
                            </ListItemIcon>
                            <ListItemText
                                primary={template.name}
                                secondary={template.description}
                                secondaryTypographyProps={{ variant: 'caption', noWrap: true, sx: { maxWidth: 200 } }}
                            />
                        </MenuItem>
                    ))}
                </Menu>

                {/* Edit Menu */}
                <Button variant="text" size="small" sx={menuButtonStyles} onClick={handleEditClick}>
                    Edit
                </Button>
                <Menu
                    anchorEl={editAnchor}
                    open={Boolean(editAnchor)}
                    onClose={handleClose}
                    MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                    elevation={1}
                    PaperProps={menuPaperProps}
                    sx={{ '& .MuiPaper-root': { minWidth: 220 } }}
                >
                    {/* Undo/Redo */}
                    <MenuItem onClick={handleUndo} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Undo fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Undo (Ctrl+Z)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleRedo} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Redo fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Redo (Ctrl+Y / Ctrl+Shift+Z)</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Cut/Copy/Paste */}
                    <MenuItem onClick={handleCut} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ContentCut fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Cut (Ctrl+X)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleCopy} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ContentCopy fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Copy (Ctrl+C)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handlePaste} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ContentPaste fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Paste (Ctrl+V)</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Select/Delete */}
                    <MenuItem onClick={handleSelectAll} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SelectAll fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Select All (Ctrl+A)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDelete} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Delete fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Delete (Del)</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Find & Replace */}
                    <MenuItem onClick={handleFindReplace} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <FindReplace fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Find & Replace... (Ctrl+F)</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Preferences */}
                    <MenuItem onClick={handleProjectSettings} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SettingsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Preferences...</ListItemText>
                    </MenuItem>
                </Menu>

                {/* View Menu */}
                <Button variant="text" size="small" sx={menuButtonStyles} onClick={handleViewClick}>
                    View
                </Button>
                <Menu
                    anchorEl={viewAnchor}
                    open={Boolean(viewAnchor)}
                    onClose={handleClose}
                    MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                    elevation={1}
                    PaperProps={menuPaperProps}
                    sx={{ '& .MuiPaper-root': { minWidth: 220 } }}
                >
                    {/* Zoom Controls */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Zoom
                        </Typography>
                    </Box>
                    <MenuItem onClick={handleZoomIn} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ZoomIn fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Zoom In (Ctrl+=)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleZoomOut} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ZoomOut fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Zoom Out (Ctrl+-)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleZoomFit} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <FitScreen fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Fit to Screen (Ctrl+0)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleZoomReset} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <CenterFocusStrong fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Reset Zoom (100%)</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Panels */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Panels
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => handleTogglePanel('Library')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Extension fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Show / Hide Library</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleTogglePanel('Properties')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SettingsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Show / Hide Properties</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleTogglePanel('Console')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Terminal fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Show / Hide Console</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleTogglePanel('Logs')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Description fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Show / Hide Logs</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleTogglePanel('Minimap')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ViewInAr fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Show / Hide Minimap</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* View Options */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            View Options
                        </Typography>
                    </Box>
                    <MenuItem onClick={handleCenterView} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <CenterFocusStrong fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Center View</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleTogglePanel('Grid')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <GridOn fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Show / Hide Grid</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handle3DViewOptions} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ThreeDRotation fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>3D View Options...</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Layout */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Layout
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => handleTogglePanel('Layout')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ViewModule fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Reset Layout</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Fullscreen */}
                    <MenuItem onClick={handleToggleFullscreen} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText>{isFullscreen ? 'Exit Fullscreen (F11)' : 'Enter Fullscreen (F11)'}</ListItemText>
                    </MenuItem>
                </Menu>

                {/* Tools Menu */}
                <Button variant="text" size="small" sx={menuButtonStyles} onClick={handleToolsClick}>
                    Tools
                </Button>
                <Menu
                    anchorEl={toolsAnchor}
                    open={Boolean(toolsAnchor)}
                    onClose={handleClose}
                    MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                    elevation={1}
                    PaperProps={menuPaperProps}
                    sx={{ '& .MuiPaper-root': { minWidth: 240 } }}
                >
                    {/* Editors */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Editors
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/flow'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <AccountTree fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Flow Editor</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/simulation'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ViewInAr fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>3D Simulator</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/simulation/world-builder'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Build fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>World Builder</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/simulation/multi-robot'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Group fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Multi-Robot Simulation</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/simulation/scenarios'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Star fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Scenario Creator</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ai/image-processing'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Image fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Image Processing</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ai/ml-training'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Psychology fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>ML Model Training</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ai/datasets'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Folder fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Dataset Manager</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ai/models'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Extension fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Model Library</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ai/reinforcement-learning'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <TrendingUp fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Reinforcement Learning</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/remote/simulators'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Computer fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Remote Simulators</ListItemText>
                    </MenuItem>
                    <Divider />
                    {/* Testing & Quality */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Testing & Quality
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/testing/builder'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Code fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Test Builder</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/testing/executor'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <PlayArrow fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Test Executor</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/testing/coverage'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <TrendingUp fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Test Coverage</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/testing/quality'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Build fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Code Quality</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/testing/performance'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Speed fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Performance Monitor</ListItemText>
                    </MenuItem>
                    <Divider />
                    {/* Advanced Robotics */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Advanced Robotics
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/advanced/slam'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Map fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>SLAM</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/advanced/path-planning'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Route fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Path Planning</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/advanced/robot-arm'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <PrecisionManufacturing fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Robot Arm Control</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/advanced/export'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Download fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Project Export</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/advanced/real-robot'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SmartToy fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Real Robot Integration</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/robot-builder'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SmartToy fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Robot Builder</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Simulation Controls */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Simulation
                        </Typography>
                    </Box>
                    <MenuItem onClick={handleRunSimulation} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <PlayArrow fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Run Simulation</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleStopSimulation} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Stop fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Stop Simulation</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Pause Simulation',
                            message: 'Simulation paused.'
                        }));
                    }} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Pause fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Pause Simulation</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Reset Simulation',
                            message: 'Simulation reset to initial state.'
                        }));
                    }} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <RestartAlt fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Reset Simulation</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Robot Operations */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Robot Operations
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/robot'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <RobotIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Robot Control</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/sensors'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Sensors fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Sensor Dashboard</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/robot-config'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SettingsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Robot Configuration</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ros2/code-generator'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Code fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>ROS2 Code Generator</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ros2/topics'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Monitor fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>ROS2 Topic Monitor</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ros2/services'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Build fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>ROS2 Service Tester</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ros2/actions'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <PlayArrow fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>ROS2 Action Monitor</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ros2/launch-editor'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Code fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>ROS2 Launch Editor</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ros2/topic-graph'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <AccountTree fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>ROS2 Topic Graph</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/ros2/packages'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Store fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>ROS2 Package Manager</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleConnectRobot} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Cable fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Connect Robot</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleManualControl} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <GamepadOutlined fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Manual Control</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Calibration Tools',
                            message: 'Robot calibration tools coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Tune fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Calibration Tools...</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Monitoring & Debugging */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Monitoring & Debugging
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/monitoring'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Monitor fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Monitoring Dashboard</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'ROS Topic Monitor',
                            message: 'ROS topic monitoring tool coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Analytics fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>ROS Topic Monitor</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Debug Console',
                            message: 'Debug console coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <BugReport fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Debug Console</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Performance Profiler',
                            message: 'Performance profiling tools coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Speed fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Performance Profiler</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Code Generation & Utilities */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Code & Utilities
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Generate Code',
                            message: 'Code generation from flow coming soon.'
                        }));
                    }} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Code fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Generate Code (ROS2)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Generate URDF',
                            message: 'URDF generation tool coming soon.'
                        }));
                    }} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Build fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Generate URDF</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Export Robot Design',
                            message: 'Export robot design as STL files.'
                        }));
                    }} disabled={!currentProject} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Download fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Export Robot Design (STL)</ListItemText>
                    </MenuItem>
                </Menu>

                {/* Marketplace Menu */}
                <Button variant="text" size="small" sx={menuButtonStyles} onClick={handleMarketplaceClick}>
                    Marketplace
                </Button>
                <Menu
                    anchorEl={marketplaceAnchor}
                    open={Boolean(marketplaceAnchor)}
                    onClose={handleClose}
                    MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                    elevation={1}
                    PaperProps={menuPaperProps}
                    sx={{ '& .MuiPaper-root': { minWidth: 240 } }}
                >
                    {/* Browse & Discover */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Browse & Discover
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/marketplace'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Store fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Browse Modules</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        if (onNavigate) onNavigate('/marketplace');
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Featured Modules',
                            message: 'Showing featured modules in marketplace.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Star fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Featured Modules</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        if (onNavigate) onNavigate('/marketplace');
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Recently Added',
                            message: 'Showing recently added modules.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <History fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Recently Added</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        if (onNavigate) onNavigate('/marketplace');
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Browse by Category',
                            message: 'Filtering modules by category.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Category fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Browse by Category</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Module Management */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Module Management
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/modules'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Extension fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Installed Modules</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Checking for Updates',
                            message: 'Checking for module updates...'
                        }));
                        // TODO: Implement update check
                        setTimeout(() => {
                            dispatch(addNotification({
                                type: 'success',
                                title: 'Update Check Complete',
                                message: 'All modules are up to date.'
                            }));
                        }, 1500);
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Update fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Check for Updates</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Module Settings',
                            message: 'Opening module settings...'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SettingsApplications fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Module Settings</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Developer Tools */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Developer Tools
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Publish Module',
                            message: 'Module publishing interface coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <FileUpload fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Publish Module...</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'My Modules',
                            message: 'Viewing your published modules.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <AccountTree fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>My Published Modules</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Utilities */}
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Refreshing Catalog',
                            message: 'Refreshing module catalog...'
                        }));
                        // TODO: Implement catalog refresh
                        setTimeout(() => {
                            dispatch(addNotification({
                                type: 'success',
                                title: 'Catalog Refreshed',
                                message: 'Module catalog has been updated.'
                            }));
                        }, 1500);
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Refresh fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Refresh Catalog</ListItemText>
                    </MenuItem>
                </Menu>

                {/* Window Menu */}
                <Button variant="text" size="small" sx={menuButtonStyles} onClick={handleWindowClick}>
                    Window
                </Button>
                <Menu
                    anchorEl={windowAnchor}
                    open={Boolean(windowAnchor)}
                    onClose={handleClose}
                    MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                    elevation={1}
                    PaperProps={menuPaperProps}
                >
                    <MenuItem onClick={() => handleTogglePanel('Console')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Terminal fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Show / Hide Console</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleTogglePanel('Logs')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Description fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Show / Hide Logs</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleTogglePanel('Properties')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SettingsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Show / Hide Properties</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleTogglePanel('Library')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Visibility fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Show / Hide Library</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />
                    <MenuItem onClick={() => handleTogglePanel('Minimap')} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ViewInAr fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Show / Hide Minimap</ListItemText>
                    </MenuItem>
                </Menu>

                {/* Spacer to push Settings and Help to the right */}
                <Box sx={{ flexGrow: 1 }} />

                {/* Settings Menu (before Help) */}
                <Button variant="text" size="small" sx={menuButtonStyles} onClick={handleSettingsClick}>
                    Settings
                </Button>
                <Menu
                    anchorEl={settingsAnchor}
                    open={Boolean(settingsAnchor)}
                    onClose={handleClose}
                    MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                    elevation={1}
                    PaperProps={menuPaperProps}
                    sx={{ '& .MuiPaper-root': { minWidth: 240 } }}
                >
                    {/* Appearance */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Appearance
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(setTheme(theme === 'dark' ? 'light' : 'dark'));
                        dispatch(addNotification({
                            type: 'success',
                            title: 'Theme Changed',
                            message: `Switched to ${theme === 'dark' ? 'light' : 'dark'} theme.`
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            {theme === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText>{theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Editor Settings */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Editor
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => {
                        handleClose();
                        if (onNavigate) onNavigate('/settings');
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Editor Settings',
                            message: 'Opening editor settings...'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Code fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Editor Preferences...</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Keyboard Shortcuts',
                            message: 'Keyboard shortcuts reference coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Keyboard fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Keyboard Shortcuts...</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Notifications */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Notifications
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => {
                        handleClose();
                        if (onNavigate) onNavigate('/settings');
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Notification Settings',
                            message: 'Opening notification settings...'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Notifications fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Notification Preferences...</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Data & Storage */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Data & Storage
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => {
                        handleClose();
                        if (onNavigate) onNavigate('/settings');
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Storage Settings',
                            message: 'Opening storage settings...'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Storage fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Storage Management...</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        if (window.confirm('Are you sure you want to clear all cached data? This cannot be undone.')) {
                            localStorage.clear();
                            dispatch(addNotification({
                                type: 'success',
                                title: 'Cache Cleared',
                                message: 'All cached data has been cleared.'
                            }));
                        }
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <ClearAll fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Clear Cache</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Backup Data',
                            message: 'Data backup functionality coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Backup fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Backup Data...</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Restore Data',
                            message: 'Data restore functionality coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Restore fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Restore Data...</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Network & Sync */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Network & Sync
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Sync Settings',
                            message: 'Cloud sync settings coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <CloudSync fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Cloud Sync Settings...</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Network Status',
                            message: 'Network status: Connected'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Wifi fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Network Status</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Security */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Security
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Security Settings',
                            message: 'Security settings coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Security fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Security & Privacy...</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Language */}
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Language Settings',
                            message: 'Language selection coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Language fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Language...</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Full Settings Page */}
                    <MenuItem onClick={() => {
                        handleClose();
                        if (onNavigate) onNavigate('/settings');
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SettingsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>All Settings...</ListItemText>
                    </MenuItem>
                </Menu>

                {/* Profile Menu */}
                <Button variant="text" size="small" sx={menuButtonStyles} onClick={handleProfileClick}>
                    Profile
                </Button>
                <Menu
                    anchorEl={profileAnchor}
                    open={Boolean(profileAnchor)}
                    onClose={handleClose}
                    MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                    elevation={1}
                    PaperProps={menuPaperProps}
                    sx={{ '& .MuiPaper-root': { minWidth: 240 } }}
                >
                    {/* Profile Info */}
                    <Box sx={{ px: 2, py: 1, bgcolor: theme === 'dark' ? 'grey.800' : 'grey.100' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                                <Person />
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                                    John Doe
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    john.doe@example.com
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Divider sx={dividerStyles} />

                    {/* Profile Management */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Profile
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => {
                        handleClose();
                        if (onNavigate) onNavigate('/profile');
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <AccountCircle fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>View Profile</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        if (onNavigate) onNavigate('/profile');
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Edit Profile',
                            message: 'Opening profile editor...'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Edit Profile</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Change Avatar',
                            message: 'Avatar upload coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <PhotoCamera fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Change Avatar</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        if (onNavigate) onNavigate('/personalization');
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Psychology fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Personalization Settings</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Account & Activity */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Account & Activity
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Account Statistics',
                            message: 'Viewing account statistics...'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Timeline fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Statistics</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Activity History',
                            message: 'Viewing activity history...'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <History fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Activity History</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Account Settings',
                            message: 'Opening account settings...'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <SettingsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Account Settings</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Security */}
                    <Box sx={{ px: 2, py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Security
                        </Typography>
                    </Box>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Change Password',
                            message: 'Password change functionality coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <VpnKey fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Change Password</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        dispatch(addNotification({
                            type: 'info',
                            title: 'API Keys',
                            message: 'API key management coming soon.'
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Badge fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>API Keys</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />

                    {/* Sign Out */}
                    <MenuItem onClick={() => {
                        handleClose();
                        if (window.confirm('Are you sure you want to sign out?')) {
                            dispatch(addNotification({
                                type: 'info',
                                title: 'Sign Out',
                                message: 'Sign out functionality coming soon.'
                            }));
                            // TODO: Implement sign out
                        }
                    }} sx={{ ...menuItemStyles, color: 'error.main' }}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'error.main' }}>
                            <Logout fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Sign Out</ListItemText>
                    </MenuItem>
                </Menu>

                {/* Help Menu (rightmost) */}
                <Button variant="text" size="small" sx={menuButtonStyles} onClick={handleHelpClick}>
                    Help
                </Button>
                <Menu
                    anchorEl={helpAnchor}
                    open={Boolean(helpAnchor)}
                    onClose={handleClose}
                    MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                    elevation={1}
                    PaperProps={menuPaperProps}
                >
                    <MenuItem onClick={() => {
                        handleClose();
                        if (onTakeTour) {
                            onTakeTour();
                        } else if (onNavigate) {
                            onNavigate('/help/tutorial');
                        }
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <School fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Take Tour</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/help/tutorial'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <HelpOutline fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Tutorial</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDocumentation} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Info fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Telix Documentation</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/help/faq'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <QuestionAnswer fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>FAQ</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); if (onNavigate) onNavigate('/help/api'); }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Code fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>API Reference</ListItemText>
                    </MenuItem>
                    <Divider sx={dividerStyles} />
                    <MenuItem onClick={() => {
                        handleClose();
                        // Open keyboard shortcuts dialog
                        dispatch(addNotification({
                            type: 'info',
                            title: 'Keyboard Shortcuts',
                            message: 'Press Ctrl+? to view keyboard shortcuts',
                        }));
                    }} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Keyboard fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Keyboard Shortcuts</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleAbout} sx={menuItemStyles}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                            <Info fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>About Telix</ListItemText>
                    </MenuItem>
                </Menu>
            </Box>

            {/* Export/Tools Submenu */}
            <Menu
                anchorEl={exportAnchor}
                open={Boolean(exportAnchor)}
                onClose={handleClose}
                MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                elevation={1}
                PaperProps={menuPaperProps}
            >
                <MenuItem onClick={handleExportSTL} sx={menuItemStyles}>
                    <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                        <Print fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Export Robot Design (STL)</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleGenerateURDF} sx={menuItemStyles}>
                    <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                        <SmartToy fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Generate URDF</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleExportCode} sx={menuItemStyles}>
                    <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                        <Code fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Export Code (Python/ROS2)</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleAITrainingData} sx={menuItemStyles}>
                    <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                        <DataObject fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>AI Training Data Viewer</ListItemText>
                </MenuItem>
            </Menu>

            {/* Hidden file input for opening projects */}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileImport}
                accept=".tlx,.json"
                hidden
                aria-hidden="true"
                aria-label="project-file-input"
            />

            {/* About Dialog */}
            <AboutDialog
                open={aboutDialogOpen}
                onClose={() => setAboutDialogOpen(false)}
            />
        </>
    );
};

export default MenuBar;